/**
 * Ops banner when deposit is verified but ProviderOpportunity was not created
 * because the marketplace experiment cohort is closed.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getSetting } from "../../systemSettings/services/systemSettingsService.js";
import { getOpportunityByOrderId } from "../services/providerOpportunityService.js";
import {
  EXPERIMENT_START_AT_KEY,
  assessExperimentCohort,
  parseSettingStringValue,
} from "../../systemSettings/utils/experimentCohort.js";
import { logWarn } from "@/shared/utils/logger.js";

const isDepositVerified = (quote) =>
  quote?.payment?.depositVerified === true ||
  quote?.status === "deposit_verified";

/**
 * @param {object} props
 * @param {string} props.orderId
 * @param {object|null|undefined} props.quote
 */
const MarketplaceCohortOpsBanner = ({ orderId, quote }) => {
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [startAtDisplay, setStartAtDisplay] = useState("");

  const evaluate = useCallback(async () => {
    if (!orderId || !isDepositVerified(quote)) {
      setVisible(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [opportunity, startAtRes] = await Promise.all([
        getOpportunityByOrderId(orderId).catch(() => null),
        getSetting(EXPERIMENT_START_AT_KEY).catch(() => null),
      ]);

      if (opportunity) {
        setVisible(false);
        return;
      }

      const startAt = parseSettingStringValue(startAtRes);
      setStartAtDisplay(startAt);
      const cohort = assessExperimentCohort({
        [EXPERIMENT_START_AT_KEY]: startAt,
      });
      setVisible(!cohort.open);
    } catch (err) {
      logWarn("MarketplaceCohortOpsBanner: failed to evaluate cohort gate", {
        orderId,
        error: err?.message || String(err),
      });
      setVisible(false);
    } finally {
      setLoading(false);
    }
  }, [orderId, quote]);

  useEffect(() => {
    evaluate();
  }, [evaluate]);

  if (loading || !visible) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      sx={{ mb: 3 }}
      data-testid="marketplace-cohort-closed-banner"
      action={
        <Button
          color="inherit"
          size="small"
          component={RouterLink}
          to="/settings"
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          Open Settings
        </Button>
      }
    >
      Deposit is verified but no ProviderOpportunity exists because the marketplace
      experiment cohort is closed
      {startAtDisplay ? (
        <> (startAt: {startAtDisplay})</>
      ) : (
        <> (marketplace.experiment.startAt is not set)</>
      )}
      . Set an ISO UTC timestamp under System Settings → Features, save, then
      re-trigger deposit verification or create the opportunity manually.
    </Alert>
  );
};

export default MarketplaceCohortOpsBanner;
